import { Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Stack, Typography, Card, CardContent, Skeleton, Checkbox, IconButton, Box } from "@mui/material";
import { DataGridPro, GridColDef, GridDensity, GridFilterModel, GridPaginationModel, GridSortModel } from "@mui/x-data-grid-pro";
import { useGetFilterdSummaryQuery, useGetTagsForSearchTermQuery, useGetTagsListQuery, useGetTagsQuery, useTagSearchTermMutation, useTagSearchTermsWithTagsBulkMutation, useUntagSearchTermMutation, useBulkRemoveClustersMutation } from "src/context/api/tags/api";
import { useCurrentWorkspace } from "src/context/reducers/app-settings";
import { useDispatch, useSelector } from "react-redux";
import { fDate } from "src/utils/format-time";
import { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    GridRowSelectionModel,
    useGridApiRef
} from "@mui/x-data-grid-pro";
import { formatNumber } from "src/utils/format-number";
import { formatCurrency } from "src/utils/format-currency";
import { useLocales } from "src/locales";
import Iconify from "src/components/iconify";
import "./datagrid.scss";
import { generateColorFromString } from "./add-tag-cell";
import GenericInfiniteScrollList from "../../generic-infinite-scroll-list";
import { enqueueSnackbar } from "notistack";
import { useForm } from "react-hook-form";
import FormProvider, { RHFAutocomplete } from "src/components/hook-form";
import { FilterPanelCustom } from "src/components/data-grid/filter-panel-custom";
import { useBoolean } from "src/hooks/use-boolean";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import CustomTooltip from "src/components/visibility/components/CustomTooltip";
import { CountryBar, CountryItem } from "src/components/country-bar";
import { useCountryData } from "src/components/visibility/hooks/useCountryData";
import { HEADER } from 'src/layouts/config-layout';
import { setFilterCountry } from "src/context/reducers/filter-settings/filterSlice";
import { countryFilterFor } from "../../explore/countryLabel";
import { CountryTabLabel } from "../../explore/CountryTabLabel";
import { EXPLORE_COLORS, PAGE_PADDING_X } from "../../explore/constants";

const schema = yup.object().shape({
    search_term: yup.string().required(),
    tags: yup.array().of(yup.string().required()).required().min(1),
});


const TagsCell = (props: any & { tags: any[], refetchTags: () => void }) => {
    const workspace = useCurrentWorkspace();
    const [anchorEl,] = useState<HTMLElement | null>(null);
    const [untagSearchTerm] = useUntagSearchTermMutation();
    const [tagSearchTerm] = useTagSearchTermMutation();

    const { data: { tags: tagsList = [] } = {} } = useGetTagsListQuery({ workspaceId: workspace?.id as number }, { skip: !workspace?.id });
    const { data: { tags: tagsForSearchTerm = [] } = {} } = useGetTagsForSearchTermQuery({ workspaceId: workspace?.id as number, searchTerm: props.row.id }, { skip: !workspace?.id || !props.row.id });
    const addTagsModal = useBoolean(false);
    const defaultValues = {
        search_term: props.row.id,
        tags: [] as string[],
    };


    const methods = useForm({
        resolver: yupResolver(schema),
        defaultValues,
    });

    const [tagsOptions, setTagsOptions] = useState<any[]>([]);
    const hasCurrentTags = useRef(false);

    useEffect(() => {
        setTagsOptions((prev) => [...prev, ...(tagsList?.filter((tag: any) => !tagsOptions.some((t: any) => t.label === tag.label)).map((tag: any) => ({
            id: tag.id,
            label: tag.name,
        }))) || []]);
    }, [tagsList]);

    useEffect(() => {
        if (tagsForSearchTerm?.length !== methods.getValues("tags").length) {
            methods.setValue("tags", tagsForSearchTerm?.map((tag: any) => tag.name) || [] as string[]);
        }
        if (hasCurrentTags.current) return;
        if (tagsForSearchTerm?.length > 0) {
            hasCurrentTags.current = true;
            setTagsOptions((prev) => [...prev, ...(tagsForSearchTerm?.map((tag: any) => ({
                id: tag.id,
                label: tag.name,
            }))) || []]);
            methods.setValue("tags", tagsForSearchTerm?.map((tag: any) => tag.name) || [] as string[]);
        }
    }, [tagsForSearchTerm]);

    const deduplicateTagsOptions = useMemo(() => {
        return Array.from(new Set(tagsOptions.map((tag: any) => tag.label))).map((label: string) => tagsOptions.find((tag: any) => tag.label === label));
    }, [tagsOptions]);

    const onSubmit = (data: any) => {
        tagSearchTerm({ workspaceId: workspace?.id as number, searchTerm: props.row.id, tags: data.tags.map((tag: any) => ({ label: tag })) })
            .unwrap()
            .then(() => {
                addTagsModal.onFalse();
                methods.reset();
                enqueueSnackbar("Searchterm clustered successfully", { variant: "success" });
            })
            .catch((error) => {
                enqueueSnackbar("Searchterm clustering failed", { variant: "error" });
            });
    }

    return (
        <Stack direction="row" justifyContent="space-between" height="100%" gap={0} sx={{ overflow: 'hidden', width: '100%', px: 0 }}>
            <Box
                sx={{
                    width: '100%',
                    height: '100%',
                    overflowX: 'auto',
                    overflowY: 'hidden',
                    scrollbarWidth: 'none',

                    '&::-webkit-scrollbar': {
                        display: 'none',
                    },
                }}
            >
                <Stack width="fit-content" direction="row" alignItems="center" py={1.5} px={1} gap={1}>

                    {props.tags.length > 0 ? (
                        props.tags.map((tag: any) => (
                            <Chip
                                key={tag.id}
                                label={tag.name}
                                size="small"
                                deleteIcon={
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="12"
                                        height="12"
                                        viewBox="0 0 12 12"
                                        fill="none"
                                    >
                                        <path
                                            fillRule="evenodd"
                                            clipRule="evenodd"
                                            d="M5.99715 6.70429L8.82429 9.53163C8.91854 9.62267 9.04478 9.67305 9.17582 9.67191C9.30685 9.67077 9.4322 9.61821 9.52486 9.52554C9.61752 9.43287 9.67008 9.30752 9.67122 9.17648C9.67235 9.04543 9.62198 8.91918 9.53095 8.82492L6.70381 5.99758L9.53095 3.17025C9.62198 3.07599 9.67235 2.94974 9.67122 2.81869C9.67008 2.68765 9.61752 2.56229 9.52486 2.46963C9.4322 2.37696 9.30685 2.3244 9.17582 2.32326C9.04478 2.32212 8.91854 2.3725 8.82429 2.46354L5.99715 5.29088L3.17002 2.46354C3.07534 2.37475 2.94983 2.32628 2.82005 2.32839C2.69028 2.33049 2.56641 2.38301 2.47466 2.47483C2.38291 2.56665 2.33049 2.69056 2.32847 2.82035C2.32645 2.95014 2.37501 3.07563 2.46386 3.17025L5.2905 5.99758L2.46336 8.82492C2.41563 8.87102 2.37756 8.92617 2.35136 8.98715C2.32517 9.04813 2.31139 9.11371 2.31081 9.18007C2.31023 9.24644 2.32288 9.31225 2.34801 9.37367C2.37313 9.43509 2.41024 9.4909 2.45717 9.53782C2.50409 9.58475 2.55989 9.62186 2.62131 9.64699C2.68273 9.67212 2.74853 9.68477 2.81489 9.68419C2.88125 9.68361 2.94683 9.66983 3.0078 9.64363C3.06877 9.61744 3.12392 9.57936 3.17002 9.53163L5.99715 6.70429Z"
                                            fill="#919EAB"
                                        />
                                    </svg>
                                }
                                variant="soft"
                                sx={{
                                    borderRadius: "32px",
                                    backgroundColor: "rgba(145, 158, 171, 0.10)",
                                    color: "#5E6D7B",
                                    maxWidth: "150px",

                                    px: "10px",
                                    py: "2px",
                                    pr: "6px",
                                    gap: "6px",
                                    minHeight: "24px",

                                    "& .MuiChip-label": {
                                        px: 0,
                                        py: 0,
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                        color: "#5E6D7B",
                                    },

                                    "& .MuiChip-label.MuiChip-labelSmall": {
                                        color: "#5E6D7B",
                                    },

                                    "& .MuiChip-deleteIcon": {
                                        width: 12,
                                        height: 12,
                                        color: "#919EAB",
                                        margin: 0,
                                    },

                                    "& .MuiChip-deleteIcon:hover": {
                                        color: "#919EAB",
                                    },
                                }}
                                onDelete={() => {
                                    untagSearchTerm({
                                        workspaceId: workspace?.id as number,
                                        searchTerm: props.row.id,
                                        tag: tag.name,
                                    })
                                        .unwrap()
                                        .then(() => {
                                            enqueueSnackbar("Search term unclustered successfully", {
                                                variant: "success",
                                            });
                                        })
                                        .catch(() => {
                                            enqueueSnackbar("Unclustering search term failed", {
                                                variant: "error",
                                            });
                                        });
                                }}
                            />
                        ))
                    ) : (
                        <Typography sx={{ color: "rgba(94, 109, 123, 0.7)" }}>--</Typography>
                    )}
                </Stack>
            </Box>

            <Button
                onClick={addTagsModal.onTrue}
                sx={{
                    borderRadius: 0,
                    m: 0,
                    p: 0,
                    color: "#2F3843",
                    backgroundColor: "rgba(240, 240, 240, 0.70)",
                    minWidth: 'fit-content',
                    "& .MuiButton-startIcon": {
                        ml: 0.5,
                        mr: 0.5,
                    },
                    "&:hover": {
                        backgroundColor: "rgba(240, 240, 240, 0.8)",
                    },
                }} variant="contained" startIcon={<Iconify sx={{ cursor: 'pointer' }} onClick={addTagsModal.onTrue} icon="mdi-light:plus" />} />
            <Dialog fullWidth maxWidth="sm" open={addTagsModal.value} onClose={addTagsModal.onFalse}>
                <FormProvider methods={methods} onSubmit={methods.handleSubmit(onSubmit)}>
                    <DialogTitle color={"rgba(51, 60, 71, 1)"}>Add clusters</DialogTitle>
                    <DialogContent sx={{ p: 2 }}>
                        <TextField size="medium" label="Search term" disabled value={props.row.id} fullWidth sx={{ mt: 2 }} />
                        <GenericInfiniteScrollList name="tags"
                            sx={{ mt: 2 }}
                            size="medium"
                            searchPlaceholder="Type and press enter to add a cluster"
                            fullWidth={true}
                            multiple={true}
                            freeSolo={true}
                            options={tagsOptions}
                            items={deduplicateTagsOptions}
                            transformValue={(valueId: string) => tagsOptions.find((tag: any) => tag.id === valueId)?.label || valueId as any}
                            customItemRenderer={(option) => (
                                <Chip
                                    label={option.label || option as any}
                                    size="small"
                                    variant="soft"
                                    sx={{
                                        backgroundColor: "rgba(145, 158, 171, 0.1)",
                                        color: "rgba(94, 109, 123, 1)"
                                    }}
                                />
                            )}
                            customTagRenderer={(option) => (
                                <Chip
                                    label={option.label || option as any}
                                    size="small"
                                    variant="soft"
                                    sx={{
                                        cursor: "pointer",
                                        marginRight: 0.5,
                                        backgroundColor: "rgba(145, 158, 171, 0.1)",
                                        color: "rgba(94, 109, 123, 1)"
                                    }}
                                    onDelete={() => {
                                        methods.setValue("tags", methods.getValues("tags").filter((tag: any) => tag.id !== option.id));
                                    }}
                                />
                            )}
                            setItems={(items) => setTagsOptions(items)}
                            getOptionLabel={(option) => (option as any).label || option as any}
                            getOptionKey={(option) => (option as any).id || option as any}
                            isOptionEqualToValue={(option, value) => {
                                const isOptionIDEqual = (option.id || option as any) === value;
                                const isOptionLabelEqual = (option.label || option as any) === value;
                                return isOptionIDEqual || isOptionLabelEqual;
                            }}
                        />
                    </DialogContent>
                    <DialogActions sx={{ justifyContent: 'flex-start' }}>
                        <Button sx={{ backgroundColor: "#181818" }} variant="contained" color="primary" type="submit">Add</Button>
                    </DialogActions>
                </FormProvider>
            </Dialog>
        </Stack>
    );
};

const useColumns = (page: number, pageSize: number, refetchTags: () => void, maxBrandSearches: any, maxAdCost: any) => {
    const workspace = useCurrentWorkspace();
    const { currentLang } = useLocales();
    const locale = currentLang.value || "en";
    return [
        {
            field: 'id',
            headerName: 'Search Term',
            headerClassName: 'header-font-color',
            flex: 1,
        },
        {
            field: 'brand_searches',
            headerName: 'Brand Searches',
            headerAlign: 'left',
            headerClassName: 'header-font-color',
            flex: 1,
            type: 'number',
            minWidth: 220,
            renderCell: (params) => {
                const percentage =
                    maxBrandSearches > 0
                        ? (params.value / maxBrandSearches) * 100
                        : 0;

                return (
                    <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1}
                        width="100%"
                    >
                        <Typography
                            sx={{
                                minWidth: 45,
                                textAlign: 'right',
                                color: '#333C47',
                                fontWeight: 500,
                                fontSize: "14px",
                            }}
                        >
                            {formatNumber(params.value)}
                        </Typography>

                        <Box
                            sx={{
                                flex: 1,
                                height: 6,
                                borderRadius: 999,
                                backgroundColor: 'transparent',
                                overflow: 'hidden',
                            }}
                        >
                            <Box
                                sx={{
                                    width: `${percentage}%`,
                                    height: '100%',
                                    borderRadius: 999,
                                    backgroundColor: '#EE7A63',
                                }}
                            />
                        </Box>
                    </Stack>
                );
            },
        },
        {
            field: 'ad_cost',
            headerName: 'Ad Cost',
            headerAlign: 'left',
            headerClassName: 'header-font-color',
            flex: 1,
            minWidth: 220,
            type: 'number',
            renderCell: (params) => {
                const percentage =
                    maxAdCost > 0
                        ? (params.value / maxAdCost) * 100
                        : 0;

                return (
                    <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1}
                        width="100%"
                    >
                        <Typography
                            sx={{
                                minWidth: 65,
                                textAlign: 'right',
                                color: '#333C47',
                                fontWeight: 500,
                                fontSize: '14px',
                            }}
                        >
                            {formatCurrency(
                                params.value,
                                workspace?.currency,
                                locale,
                                true,
                                2
                            )}
                        </Typography>

                        <Box
                            sx={{
                                flex: 1,
                                height: 6,
                                borderRadius: 999,
                                backgroundColor: 'transparent',
                                overflow: 'hidden',
                            }}
                        >
                            <Box
                                sx={{
                                    width: `${percentage}%`,
                                    height: '100%',
                                    borderRadius: 999,
                                    backgroundColor: '#DAA335',
                                }}
                            />
                        </Box>
                    </Stack>
                );
            },
        },
        {
            field: 'blended_ctr',
            type: 'number',
            headerAlign: 'left',
            headerName: 'Blended CTR',
            headerClassName: 'header-font-color',
            flex: 1,
            minWidth: 220,
            renderCell: (params) => {
                const value = params.value || 0;
                const percentage = Math.min(value, 100);

                return (
                    <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1}
                        width="100%"
                    >
                        <Typography
                            sx={{
                                minWidth: 50,
                                textAlign: 'right',
                                color: '#333C47',
                                fontWeight: 500,
                                fontSize: "14px",
                            }}
                        >
                            {value.toFixed(1)}%
                        </Typography>

                        <Box
                            sx={{
                                flex: 1,
                                height: 6,
                                borderRadius: 999,
                                backgroundColor: 'transparent',
                                overflow: 'hidden',
                            }}
                        >
                            <Box
                                sx={{
                                    width: `${percentage}%`,
                                    height: '100%',
                                    borderRadius: 999,
                                    backgroundColor: '#5119B7',
                                }}
                            />
                        </Box>
                    </Stack>
                );
            },
        },
        {
            field: 'tags',
            headerClassName: 'header-font-color',
            headerName: 'Tags',
            flex: 1,
            minWidth: 400,
            sortable: false,
            cellClassName: 'tags-cell',
            renderCell: (params) => <TagsCell tags={params.row.tags || []} refetchTags={refetchTags} row={params.row} />,
        },
    ] as GridColDef[];
};

const CustomToolbar = ({ apiRef, fullResult, filterModel, multiTaggable, rowSelectionModel, filterdSummary, refetchTags, rows, ...props }: any & { filterModel: GridFilterModel, multiTaggable: boolean, rowSelectionModel: GridRowSelectionModel, filterdSummary: any, refetchTags: () => void, rows: any[] }) => {
    const workspace = useCurrentWorkspace();
    const { data: { tags: tagsList = [] } = {} } = useGetTagsListQuery({ workspaceId: workspace?.id as number });
    const [tagsOptions, setTagsOptions] = useState<any[]>([]);
    const [tagSearchTermsWithTagsBulk] = useTagSearchTermsWithTagsBulkMutation();
    const [bulkRemoveClusters] = useBulkRemoveClustersMutation();
    const [open, setOpen] = useState<boolean>(false);
    const handleClose = () => {
        setOpen(false);
    };
    const [openRemove, setOpenRemove] = useState(false);
    const [clustersToRemove, setClustersToRemove] = useState<any[]>([]);

    const selectedClusters = useMemo(() => {
        const selectedRows = rows.filter((row: any) =>
            rowSelectionModel.includes(row.id)
        );

        const clusters = Array.from(
            new Map(
                selectedRows
                    .flatMap((row: any) => row.tags || [])
                    .map((tag: any) => [tag.id, tag])
            ).values()
        );

        return clusters;
    }, [rows, rowSelectionModel]);

    useEffect(() => {
        if (openRemove) {
            setClustersToRemove(selectedClusters);
        }
    }, [openRemove, selectedClusters]);

    const filterStore = useSelector((state: any) => state.filters);
    const filterdSummaryParams = {
        workspaceId: workspace?.id as number,
        start: fDate(filterStore.start, "yyyy-MM-dd"),
        end: fDate(filterStore.end, "yyyy-MM-dd"),
        keywords: filterStore.keywords,
        countries: filterStore.countries,
        devices: filterStore.devices,
        platforms: filterStore.platforms,
        filterModel,
    }

    const formValuesFactory = useMemo(() => {
        const searchTerms = fullResult ? filterdSummary?.selected.map((row: any) => row.id) : rowSelectionModel.map((row: any) => ({ id: row, label: row }))
        return {
            searchTerms,
            tags: [] as { label: string }[],
        }
    }, [fullResult, filterdSummary?.selected, rowSelectionModel]);

    const methods = useForm({
        defaultValues: formValuesFactory,
    });

    useEffect(() => {
        if (tagsList?.length > 0 && tagsOptions.length === 0 || tagsList?.length != tagsOptions.length) {
            setTagsOptions(tagsList.map((tag: any) => ({ id: tag.id, label: tag.name })));
        }
    }, [tagsList]);

    useEffect(() => {
        if (rowSelectionModel.length > 0) {
            methods.setValue(
                "searchTerms",
                rowSelectionModel.map((row: any) => ({
                    id: row,
                    label: row,
                }))
            );
        } else {
            methods.setValue("searchTerms", []);
        }
    }, [rowSelectionModel]);


    const handleSubmit = (data: any) => {
        tagSearchTermsWithTagsBulk({
            workspaceId: workspace?.id as number,
            addTags: data.tags,
            searchTerms: fullResult ? undefined : data.searchTerms,
            ...(!fullResult ? {} : filterdSummaryParams)
        })
            .unwrap()
            .then(() => {
                handleClose();
                enqueueSnackbar("Search terms clustered successfully", { variant: "success" });
            })
            .catch((error) => {
                enqueueSnackbar("Clustering search terms failed", { variant: "error" });
            });
    }

    const chipProps = {
        sx: {
            "&:hover": {
                backgroundColor: "rgba(145, 158, 171, 0.16)",
            },
            my: 0,
            py: 0,
            backgroundColor: "rgba(145, 158, 171, 0.16)",
            color: "rgba(94, 109, 123, 1)",
            maxHeight: "24px"
        },
        onDelete: undefined,
    }

    const handleRemoveClusters = () => {
        bulkRemoveClusters({
            workspaceId: workspace?.id as number,
            tags: clustersToRemove.map((cluster) => cluster.name),
            searchTerms: fullResult
                ? filterdSummary?.selected.map((row: any) => row.id)
                : rowSelectionModel,
        })
            .unwrap()
            .then(() => {
                enqueueSnackbar(
                    "Clusters removed successfully",
                    { variant: "success" }
                );

                setOpenRemove(false);
            })
            .catch(() => {
                enqueueSnackbar(
                    "Removing clusters failed",
                    { variant: "error" }
                );
            });
    };

    return (
        <Stack
            px="20px"
            height="40px"
            justifyContent="center"
        >
            <Stack
                className="custom-toolbar"
                direction="row"
                alignItems="center"
                justifyContent="center"
                mx={0}
                pr={0}
                gap={1}
                sx={{ height: "100%" }}
            >
                <Stack
                    direction="row"
                    alignItems="center"
                    spacing={0.5}
                    sx={{ height: "100%" }}
                >
                    <CustomTooltip title={"Apply Filters"} arrow placement="top">
                        <IconButton
                            onClick={() => apiRef.current?.showFilterPanel()}
                            sx={{
                                width: 26,
                                height: 26,
                                padding: 0,
                                color: "#919EAB",
                                "&:hover": {
                                    backgroundColor: "action.hover",
                                },
                            }}
                        >
                            <Iconify
                                icon="ic:round-filter-list"
                                width={18}
                                height={18}
                            />
                        </IconButton>
                    </CustomTooltip>
                </Stack>
                <Stack flexGrow={1} />

                {(rowSelectionModel.length > 0 || fullResult) && (
                    <Button
                        sx={{ backgroundColor: 'rgba(145, 158, 171, 0.20)', color: "#333C47" }}
                        variant="contained"
                        onClick={() => setOpenRemove(true)}
                        size="small"
                    >
                        Remove
                    </Button>
                )}
                <Button variant={"contained"} size="small" sx={{ backgroundColor: "#181818", px: 1.5, mx: 0, my: 1 }} onClick={() => setOpen(true)}>Add cluster</Button>
            </Stack>
            <Dialog fullWidth maxWidth="sm" sx={{ padding: "24px" }} open={open} onClose={handleClose}>
                <DialogTitle
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        py: 3,
                        pr: 2,
                    }}
                >
                    <Typography
                        sx={{
                            color: "#333C47",
                            fontFamily: "Public Sans",
                            fontSize: "18px",
                            fontWeight: 500,
                            lineHeight: "28px",
                        }}
                    >
                        Add clusters
                    </Typography>

                    <IconButton
                        onClick={handleClose}
                        sx={{
                            color: "#919EAB",
                            p: 0.5,
                        }}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 10 10" fill="none">
                            <path d="M9 1L1 9M1 1L9 9" stroke="#5E6D7B" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                        </svg>
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ p: "24px", }}>
                    <FormProvider methods={methods} onSubmit={methods.handleSubmit(handleSubmit)}>
                        <RHFAutocomplete
                            multiple={true}
                            freeSolo={true}
                            name="searchTerms"
                            sx={{ mt: 2 }}
                            disabled={fullResult}
                            renderInput={(params) => <TextField label={fullResult ? "Search terms" : "Add / Paste Search terms"} placeholder={fullResult ? "Search terms" : "Add / Paste Search terms"} {...params} />}
                            renderTags={(value, getTagProps) => {
                                const tags = value.slice(0, 2).map((item: any, index: number) => (
                                    <Chip  {...getTagProps({ index })} label={item.label || item} {...chipProps} key={index} />
                                ))
                                const remainingCount = fullResult ? filterdSummary?.total_search_terms - 2 : value.length - 2;
                                if (remainingCount > 0) {
                                    return [
                                        ...tags,
                                        <Chip label={`+${remainingCount}`} {...chipProps} key="remaining" />
                                    ]
                                }
                                return tags;
                            }}
                        />
                        <GenericInfiniteScrollList name="tags"
                            sx={{ mt: 2 }}
                            searchPlaceholder="Type and press enter to add a cluster"
                            fullWidth={true}
                            multiple={true}
                            freeSolo={true}
                            size="medium"
                            options={tagsOptions}
                            items={tagsOptions}
                            autoHighlight={false}
                            transformValue={(valueId: string) => tagsOptions.find((tag: any) => tag.id === valueId)?.label || valueId as any}
                            customItemRenderer={(option) => (
                                <Chip
                                    label={option.label || option as any}
                                    size="small"
                                    variant="soft"
                                    sx={{
                                        backgroundColor: option.label || option as any
                                            ? generateColorFromString(String(option.label || option))
                                            : undefined,
                                        color: "#fff"
                                    }}
                                />
                            )}
                            customTagRenderer={(option) => (
                                <Chip
                                    label={option.label || option as any}
                                    size="small"
                                    variant="soft"
                                    sx={{
                                        cursor: "pointer",
                                        marginRight: 0.5,
                                        backgroundColor: option.label || option as any
                                            ? generateColorFromString(String(option.label || option))
                                            : undefined,
                                        color: "#fff"
                                    }}
                                    onDelete={() => {
                                        methods.setValue("tags", methods.getValues("tags").filter((tag: any) => tag.id !== option.id));
                                    }}
                                />
                            )}
                            setItems={(items) => setTagsOptions(items)}
                            getOptionLabel={(option) => (option as any).label || option as any}
                            getOptionKey={(option) => (option as any).id || option as any}
                            isOptionEqualToValue={(option, value) => (option.id || option as any) === (value.id || value as any)}
                        />
                        <Button type="submit" sx={{ mt: "24px", backgroundColor: "#181818" }} variant="contained" color="primary">Add</Button>
                    </FormProvider>
                </DialogContent>
            </Dialog>

            <Dialog
                fullWidth
                maxWidth="sm"
                open={openRemove}
                onClose={() => setOpenRemove(false)}
            >
                <DialogTitle
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        py: 3,
                        pr: 2,
                    }}
                >
                    <Typography
                        sx={{
                            color: "#333C47",
                            fontFamily: "Public Sans",
                            fontSize: "18px",
                            fontWeight: 600,
                            lineHeight: "28px",
                        }}
                    >
                        Remove Clusters
                    </Typography>

                    <IconButton
                        onClick={() => setOpenRemove(false)}
                        sx={{
                            color: "#919EAB",
                            p: 0.5,
                        }}
                    >
                        <Iconify icon="mingcute:close-line" width={20} />
                    </IconButton>
                </DialogTitle>

                <DialogContent sx={{ p: "24px" }}>
                    <Typography
                        sx={{
                            color: "rgba(94, 109, 123, 1)",
                            fontSize: "12px",
                            mb: 1,
                        }}
                    >
                        Clusters to remove
                    </Typography>

                    <Stack
                        sx={{
                            border: "1px solid rgba(145, 158, 171, 0.32)",
                            borderRadius: "8px",
                            minHeight: "45px",
                            p: 1,
                        }}
                        direction="row"
                        gap={1}
                        flexWrap="wrap"
                    >
                        {clustersToRemove.map((cluster: any) => (
                            <Chip
                                key={cluster.id}
                                label={cluster.name}
                                size="small"
                                variant="soft"
                                onDelete={() => {
                                    setClustersToRemove((prev) =>
                                        prev.filter((c) => c.id !== cluster.id)
                                    );
                                }}
                                sx={{
                                    backgroundColor: "#EAEAEA",
                                    color: "#333C47",
                                }}
                            />
                        ))}
                    </Stack>
                </DialogContent>

                <DialogActions
                    sx={{
                        justifyContent: "flex-start",
                        p: "24px",
                    }}
                >
                    <Button
                        sx={{ backgroundColor: "#161C24", color: "white" }}
                        variant="contained"
                        onClick={handleRemoveClusters}
                    >
                        Remove
                    </Button>
                </DialogActions>
            </Dialog>
        </Stack>
    )
}

export default function TagManagerGrid() {
    const workspace = useCurrentWorkspace();
    const filterStore = useSelector((state: any) => state.filters);
    const skipQuery = !filterStore.start || !filterStore.end || !workspace?.id;
    const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({ page: 0, pageSize: 20 });
    const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>([]);
    const [sortModel, setSortModel] = useState<GridSortModel>([{ field: "brand_searches", sort: "desc" }]);
    const [filterModel, setFilterModel] = useState<GridFilterModel>({ items: [] });
    const [multiTaggable, setMultiTaggable] = useState<boolean>(false);
    const [density, setDensity] = useState<GridDensity>("standard");
    const [fullResult, selectFullResult] = useState<boolean>(false);

    const apiRef = useGridApiRef();

    const dispatch = useDispatch();

    // Pays + coverage % via la query Visibility (front-only). Triés couverture desc.
    const { countriesData, isLoading: isLoadingCountries } = useCountryData();
    // Liste FIGÉE au 1er chargement : useCountryData relit filterStore.countries, donc dès qu'on
    // sélectionne un pays elle se réduirait à ce seul pays et le bandeau disparaîtrait
    // (CountryBar renvoie null si <= 1 pays). On fige la liste complète (coverage % sur l'ensemble).
    const [countryList, setCountryList] = useState<CountryItem[]>([]);
    // Défaut à l'atterrissage : l'onglet « All » (vue agrégée tous pays), comme le Funnel.
    // La CountryBar utilise le sentinel 'ALL' (cf. son mode includeAll).
    const [selectedCountry, setSelectedCountry]: any = useState<string | null>('ALL');

    // Au 1er chargement : fige la liste complète et applique le filtre « All ».
    // Le endpoint /data filtre par NOM de pays (region_name), pas par code — cf. le consumer
    // canonique useCountryData dans visibility/search-terms.tsx. countryFilterFor envoie la
    // liste complète pour « All » (un tableau vide renverrait 0 ligne via IN UNNEST).
    useEffect(() => {
        if (countryList.length === 0 && countriesData.length > 0) {
            setCountryList(countriesData);
            const allNames = countriesData.map((c) => c.country_name);
            dispatch(setFilterCountry(countryFilterFor('ALL', allNames)));
        }
    }, [countriesData, countryList.length, dispatch]);

    const filterdSummaryParams = {
        workspaceId: workspace?.id as number,
        start: fDate(filterStore.start, "yyyy-MM-dd"),
        end: fDate(filterStore.end, "yyyy-MM-dd"),
        keywords: filterStore.keywords,
        countries: selectedCountry,
        devices: filterStore.devices,
        platforms: filterStore.platforms,
        page: paginationModel.page,
        pageSize: paginationModel.pageSize,
        filterModel,
        tags: filterStore.tags.map((tag: any) => tag.label),
        sortField: sortModel[0]?.field,
        sortOrder: sortModel[0]?.sort as string,
    }

    const { data, isLoading, isFetching, isUninitialized, refetch: refetchTags } = useGetTagsQuery(filterdSummaryParams, { skip: skipQuery });
    const { data: filterdSummary } = useGetFilterdSummaryQuery(filterdSummaryParams, { skip: skipQuery });

    const maxBrandSearches = useMemo(() => {
        return Math.max(
            0,
            ...(data?.data || []).map(
                (row: any) => row.brand_searches || 0
            )
        );
    }, [data?.data]);

    const maxAdCost = useMemo(() => {
        return Math.max(
            0,
            ...(data?.data || []).map(
                (row: any) => row.ad_cost || 0
            )
        );
    }, [data?.data]);

    useEffect(() => {
        if (rowSelectionModel.length > 0) {
            setMultiTaggable(true);
        } else {
            setMultiTaggable(false);
        }
    }, [rowSelectionModel]);

    useEffect(() => {
        // Leaving "select all" must not clear the selection here: the grid already emitted
        // the right model (all minus the unchecked row, or nothing for the header checkbox).
        if (!fullResult) return;

        if (isFetching || !data?.data) {
            // on attend que les données arrivent
            return;
        }

        setRowSelectionModel(data.data.map((row: any) => row.id));
        const desiredText =
            fullResult && filterdSummary?.total_search_terms != null
                ? `${filterdSummary.total_search_terms} rows selected`
                : undefined;

        const update = () => {
            const el = document.querySelector<HTMLElement>(".MuiDataGrid-selectedRowCount");
            if (!el) return false;
            if (desiredText) el.textContent = desiredText;
            return true;
        };

        // 1) Tentative immédiate (si déjà rendu)
        if (update()) return;

        // 2) Sinon, on observe le DOM jusqu’à apparition (puis on stop)
        const observer = new MutationObserver(() => {
            if (update()) observer.disconnect();
        });
        observer.observe(document.body, { childList: true, subtree: true });

        return () => observer.disconnect();
    }, [
        fullResult,
        isFetching,
        data?.data,
    ]);

    const CustomCheckbox = forwardRef(function CustomCheckboxFactory(props: any, ref) {
        const onChange = useCallback((e: any, checked: boolean) => {
            if (e.target.name === 'select_all_rows') {
                selectFullResult(checked);
            }
            else if (checked === false) {
                selectFullResult(false);
            }
            props.onChange?.(e, checked);
        }, []);

        return (
            <Checkbox
                ref={ref}
                {...props}
                sx={{ ...props.sx, pl: 1.5 }}
                onChange={onChange}
            />
        );
    })


    const countryBarLoading =
        isLoading ||
        isFetching ||
        isUninitialized ||
        (isLoadingCountries && countryList.length === 0);

    return (
        <Box>
            <CountryBar
                countries={countryList}
                selectedCountry={selectedCountry}
                onCountryChange={setSelectedCountry}
                includeAll
                loading={countryList.length === 0 || countryBarLoading}
                // +6 vs the old HEADER.H_DESKTOP*3: the secondary nav grew (title 52→54,
                // filter bar 52→56), so the bar and its content spacer shift down to match.
                backgroundColor="background.paper"
                stickyTop={HEADER.H_DESKTOP * 2}
                topGap={60}
                reserveGapWhenHidden
                contentPaddingX={PAGE_PADDING_X}
                tabSpacing={12}
                selectedColor={'#9982CC'}
                selectedBorderWidth={2}
                renderCountryLabel={(c, isSelected) => (
                    <CountryTabLabel country={c} isSelected={isSelected} />
                )}
            />
            <Card sx={{
                "& .MuiCardContent-root:last-child": { paddingBottom: "0 !important" },
                border: "1px solid rgba(145, 158, 171, 0.20)",
                borderRadius: "8px",
                mt: countryList.length > 1 ? 0 : 1,
            }}>
                <CardContent sx={{
                    px: 0,
                    py: 0,
                    paddingBottom: "24px",
                    overflow: "visible",
                    minHeight: 600, // laisse la hauteur fixe pour éviter les sauts
                    position: "relative"
                }}>
                    {/* Skeleton superposé */}
                    {(isLoading || isFetching || isUninitialized || isLoadingCountries && countryList.length === 0) && (
                        <Skeleton
                            variant="rectangular"
                            height="100%"
                            width="100%"
                            animation="wave"
                            sx={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                zIndex: 10
                            }}
                        />
                    )}
                    <DataGridPro
                        sx={{
                            height: "689px",
                            "& .MuiTablePagination-select": {
                                border: "1px solid rgba(145, 158, 171, 0.20)",
                                borderRadius: "6px",
                                backgroundColor: "transparent",
                                minWidth: 56,
                            },
                            "& .MuiTablePagination-actions .MuiIconButton-root.Mui-disabled": {
                                color: "#919EAB",
                                opacity: 0.48,
                            },
                            "& .MuiTablePagination-select .MuiSelect-select": {
                                padding: "6px 30px 6px 12px",
                                color: "#919EAB",
                            },

                            "& .MuiTablePagination-select .MuiSvgIcon-root": {
                                color: "#919EAB",
                            },
                            "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": {
                                outline: "none",
                                backgroundColor: "transparent",
                            },

                            "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within": {
                                outline: "none",
                                backgroundColor: "transparent",
                            },

                            "& .MuiTablePagination-root": {
                                color: "#919EAB",
                            },
                            "& .MuiDataGrid-cell": {
                                borderRight: "none !important",
                                borderBottom: "none !important",
                            },
                            "& .MuiDataGrid-columnSeparator": {
                                display: "none !important",
                            },

                            "& .MuiDataGrid-row": {
                                borderBottom: "none !important",
                            },
                            "& .MuiTablePagination-toolbar": {
                                minHeight: 40,
                                height: 40,
                                color: "#919EAB",
                                px: "24px"
                            },

                            "& .MuiDataGrid-footerContainer": {
                                minHeight: "40px !important",
                                height: "40px !important",
                                backgroundColor: "#FAFAFA",
                            },

                            "& .MuiTablePagination-selectLabel, \
                            & .MuiTablePagination-displayedRows": {
                                color: "#919EAB",
                            },

                            "& .MuiTablePagination-selectIcon, \
                            & .MuiTablePagination-actions button": {
                                color: "#919EAB",
                            },

                            "& .MuiDataGrid-columnHeader": {
                                backgroundColor: "#F4F5F7",
                                borderLeft: "1px solid #e9ecee",
                                borderRight: "none !important",
                                borderTop: "none !important",
                                borderBottom: "none !important",
                            },
                            opacity: (isLoading || isFetching || isUninitialized) ? 0.5 : 1,
                        }}
                        classes={{
                            root: "search-term-grid",
                            columnHeaders: "search-term-grid-column-headers",
                            columnHeader: "search-term-grid-column-header",
                        }}
                        pageSizeOptions={[20, 50, 100]}
                        columnHeaderHeight={36}
                        rowHeight={44}
                        rows={data?.data || []}
                        columns={useColumns(paginationModel.page, paginationModel.pageSize, refetchTags, maxBrandSearches, maxAdCost)}
                        paginationModel={paginationModel}
                        onPaginationModelChange={setPaginationModel}
                        apiRef={apiRef}
                        disableMultipleColumnsSorting={true}
                        rowSelectionModel={rowSelectionModel}
                        onRowSelectionModelChange={(newSelectionModel) => setRowSelectionModel([...newSelectionModel])}
                        disableRowSelectionOnClick
                        checkboxSelection
                        filterModel={filterModel}
                        onFilterModelChange={setFilterModel}
                        density={density}
                        onDensityChange={setDensity}
                        filterMode="server"
                        sortingMode="server"
                        sortingOrder={['desc', 'asc']}
                        sortModel={sortModel}
                        onSortModelChange={setSortModel}
                        slots={{
                            filterPanel: (props) => <FilterPanelCustom {...props} getColumnForNewFilter={(args) => args ? args.columns[0].field : undefined} />,
                            toolbar: CustomToolbar,
                            baseCheckbox: CustomCheckbox,
                        }}

                        slotProps={{
                            toolbar: {
                                fullResult,
                                printOptions: { disableToolbarButton: true },
                                csvOptions: { disableToolbarButton: true },
                                filterModel,
                                multiTaggable,
                                rowSelectionModel,
                                filterdSummary,
                                refetchTags,
                                apiRef: apiRef,
                                rows: data?.data || [],
                            } as any,
                        }}
                        pagination
                        paginationMode="server"
                        rowCount={data?.total_rows || 0}
                    />
                </CardContent>
            </Card>
        </Box>
    );

}